import { z } from 'zod';
import chromeJson from '@/data/site-chrome.json';

/**
 * Shared site chrome (header + footer) content. Link inventory extracted from
 * the live WP header/footer templates (Elementor 432318/153391) — see
 * docs/wordpress-header-footer-map.md. Every destination that exists in the
 * live nav exists here.
 */

const linkSchema = z.object({ label: z.string(), href: z.string(), bold: z.boolean().optional() });

const imageSchema = z.object({
  src: z.string(),
  width: z.number(),
  height: z.number(),
});

const menuGroupSchema = z.object({
  heading: z.string(),
  headingHref: z.string().optional(),
  links: z.array(linkSchema),
});

const menuSchema = z.object({
  label: z.string(),
  href: z.string().optional(),
  groups: z.array(menuGroupSchema),
  /** A button rendered at the foot of the panel — e.g. "View all locations". */
  cta: linkSchema.pick({ label: true, href: true }).optional(),
  /** A short line beside the panel's CTA — e.g. "Serving 130+ cities nationwide". */
  note: z.string().optional(),
  /** On phones, render just the top-level link (hide the sub-links) — the desktop
   * megamenu's long list would bloat the mobile drawer. Needs `href`. */
  collapseOnMobile: z.boolean().optional(),
});

const siteChromeSchema = z.object({
  brand: z.object({
    name: z.string(),
    phone: z.string(),
    phoneHref: z.string(),
    salesEmail: z.string(),
    clientPortalUrl: z.string(),
    logo: imageSchema,
    logoWhite: imageSchema,
    tagline: z.string(),
    address: z.object({ heading: z.string(), lines: z.array(z.string()) }),
    trustpilot: z.object({
      /**
       * TrustScore to one decimal and its word ("Excellent"), as the TrustBox
       * prints them. The pre-load facade (TrustpilotMicroFacade) shows these
       * until the real widget takes over, so keep them equal to the live feed:
       * `trustScore` and `starsString` at
       * https://widget.trustpilot.com/trustbox-data/<microTemplateId>?businessUnitId=<businessUnitId>&locale=en-US
       * and bump `checked` when you do.
       */
      score: z.string(),
      outOf: z.string(),
      label: z.string(),
      reviewsUrl: z.string(),
      /** Formatted count, e.g. "1,785"; hand-checked on the profile, see `checked`. */
      reviewCount: z.string(),
      /** Percent of reviews per star, from the public profile. */
      distribution: z.object({ '5': z.number(), '4': z.number(), '3': z.number(), '2': z.number(), '1': z.number() }),
      /** ISO date the score, label, count and distribution were last read off Trustpilot. */
      checked: z.string().optional(),
      businessUnitId: z.string(),
      microTemplateId: z.string(),
      microToken: z.string(),
      carouselTemplateId: z.string(),
    }),
  }),
  partners: z.array(imageSchema.extend({ label: z.string() })),
  menus: z.array(menuSchema),
  quoteCta: linkSchema,
  footer: z.object({
    programColumns: z.object({ heading: z.string(), links: z.array(linkSchema) }),
    companyColumn: z.object({ heading: z.string(), links: z.array(linkSchema) }),
    social: z.array(linkSchema),
    legalLinks: z.array(linkSchema),
    legal: z.string(),
  }),
});

export type SiteChrome = z.infer<typeof siteChromeSchema>;
export type SiteMenu = SiteChrome['menus'][number];

export const siteChrome: SiteChrome = siteChromeSchema.parse(chromeJson);
